import type { DocumentV1 } from '@alpod-ai-writer/backend';
import { AIChatPlugin, AIPlugin } from '@alpod-ai-writer/plate-plugins/ai/react';
import { CitationInputPlugin, CitationPlugin } from '@alpod-ai-writer/plate-plugins/citations/react';
import { FootnoteEntryPlugin } from '@alpod-ai-writer/plate-plugins/footnotes/react';
import { LinkPlugin } from '@alpod-ai-writer/plate-plugins/link/react';
import { NodeIdPlugin } from '@alpod-ai-writer/plate-plugins/node-id';
import { ShortcutInputPlugin, ShortcutPlugin } from '@alpod-ai-writer/plate-plugins/shortcuts/react';
import { SuggestionPlugin } from '@alpod-ai-writer/plate-plugins/suggestion/react';
import type { DocumentConfig } from '@alpod-ai-writer/shared';
import type { CursorData } from '@alpod-ai-writer/shared';
import { withProps } from '@udecode/cn';
import { type Value } from '@udecode/plate';
import { ParagraphPlugin, PlateElement, PlateLeaf, usePlateEditor } from '@udecode/plate/react';
import { BasicMarksPlugin, BoldPlugin, ItalicPlugin, SubscriptPlugin, SuperscriptPlugin, UnderlinePlugin } from '@udecode/plate-basic-marks/react';
import { SoftBreakPlugin } from '@udecode/plate-break/react';
import { CommentsPlugin } from '@udecode/plate-comments/react';
import { DocxPlugin } from '@udecode/plate-docx';
import { HighlightPlugin } from '@udecode/plate-highlight/react';
import { JuicePlugin } from '@udecode/plate-juice';
import { MarkdownPlugin, remarkDefaultTextRules } from '@udecode/plate-markdown';
import { BlockSelectionPlugin } from '@udecode/plate-selection/react';
import { TrailingBlockPlugin } from '@udecode/plate-trailing-block';

import { AIMenu } from '../components/common/aiMenu.component';
import { BlockDiscussion } from '../components/common/blockDiscussion.component';
import { BlockSelection } from '../components/common/blockSelection.component';
import { AILeaf } from '../components/elements/aiLeaf.component';
import { CitationElement } from '../components/elements/citationElement.component';
import { CitationInputElement } from '../components/elements/citationInputElement.component';
import { CommentLeaf } from '../components/elements/commentLeaf.component';
import { FootnoteEntryElement } from '../components/elements/footnoteEntryElement.component';
import { LinkElement } from '../components/elements/linkElement.component';
import { ShortcutElement } from '../components/elements/shortcutElement.component';
import { ShortcutInputElement } from '../components/elements/shortcutInputElement.component';
import { SuggestionLeaf } from '../components/elements/suggestionLeaf.component';
import { SuggestionBelowNodes } from '../components/elements/suggestionLineBreak.component';
import { CitationFloatingToolbar } from '../components/toolbar/citationFloatingToolbar.component';
import { LinkFloatingToolbar } from '../components/toolbar/linkFloatingToolbar.component';
import { commentsPlugin } from '../plugins/commentsPlugin';
import { suggestionPlugin } from '../plugins/suggestionPlugin';
import { Prompt_SystemBlockSelecting, Prompt_SystemDefault, Prompt_SystemSelecting, Prompt_UserBlockSelecting, Prompt_UserDefault, Prompt_UserSelecting } from '../prompts';
import { useAiDisclaimer } from './useAiDisclaimer.hook';

export const basicNodesPlugins = [
    BasicMarksPlugin,
    HighlightPlugin,
    LinkPlugin.configure({
        options: {
            forceSubmit: true
        },
        render: { afterEditable: () => <LinkFloatingToolbar /> },
    }),
    SoftBreakPlugin.configure({
        options: {
            rules: [
                { hotkey: 'shift+enter' },
            ],
        },
    }),
    // SelectOnBackspacePlugin.configure({
    //     options: {
    //         query: {
    //             allow: [
    //                 // ImagePlugin.key,
    //             ],
    //         },
    //     },
    // }),
    MarkdownPlugin.configure({ options: {
        indentList: true,
        textRules: {
            ...remarkDefaultTextRules,
            inlineCode: {
                mark: () => 'highlight',
            }
        }
    } }),
    JuicePlugin,
    DocxPlugin
] as const;

export interface InlineEditorOptions {
    document: DocumentV1.Record;
    cursorData: CursorData;
    initialValue?: Value;
    addTrailingBlock?: boolean;
}

export function useInlineEditor(opts: InlineEditorOptions) {
    const { useAiFeature } = useAiDisclaimer();

    const docConfig = opts.document.template?.config as DocumentConfig;

    const editor = usePlateEditor({
        value: opts.initialValue,
        plugins: [
            ...basicNodesPlugins,
            CitationPlugin.configure({
                options: {
                    insertSpaceAfterCitation: true,
                    trigger: '@',
                    pageNumberPrefix: docConfig.citationPageNumberPrefix ?? ''
                },
                render: { afterEditable: () => <CitationFloatingToolbar /> },
            }),
            ShortcutPlugin.configure({ options: { insertSpaceAfterShortcut: true, trigger: '#' } }),
            suggestionPlugin.configure({
                options: {
                    currentUserId: opts.cursorData.identityId
                },
                render: { belowNodes: SuggestionBelowNodes as any },
            }),
            commentsPlugin.configure({
                render: { aboveNodes: BlockDiscussion as any }
            }),
            BlockSelectionPlugin.configure({
                render: {
                    belowRootNodes: (props) => {
                        if (!props.className?.includes('slate-selectable')) return null;
                        return <BlockSelection />;
                    },
                },
            }),
            FootnoteEntryPlugin,
            AIPlugin,
            AIChatPlugin.configure({
                render: { afterEditable: () => <AIMenu /> },
                options: {
                    useFeature: useAiFeature,
                    systemTemplate({ isBlockSelecting, isSelecting }) {
                        return isBlockSelecting
                            ? Prompt_SystemBlockSelecting(opts)
                            : isSelecting
                                ? Prompt_SystemSelecting(opts)
                                : Prompt_SystemDefault(opts);
                    },
                    promptTemplate({ isBlockSelecting, isSelecting }) {
                        return isBlockSelecting
                            ? Prompt_UserBlockSelecting(opts)
                            : isSelecting
                                ? Prompt_UserSelecting(opts)
                                : Prompt_UserDefault(opts);
                    }
                }
            }),
            ...(opts.addTrailingBlock
                ? [
                    TrailingBlockPlugin.configure(() => ({
                        options: {
                            level: 0,
                            type: 'p'
                        }
                    }))
                ]
                : []),
            NodeIdPlugin
        ],
        override: {
            components: {
                [BoldPlugin.key]: withProps(PlateLeaf, { as: 'strong' }),
                [ItalicPlugin.key]: withProps(PlateLeaf, { as: 'em' }),
                [UnderlinePlugin.key]: withProps(PlateLeaf, { as: 'u' }),
                [SubscriptPlugin.key]: withProps(PlateLeaf, { as: 'sub' }),
                [SuperscriptPlugin.key]: withProps(PlateLeaf, { as: 'sup' }),
                [HighlightPlugin.key]: withProps(PlateLeaf, { as: 'mark', className: 'bg-yellow-200' }),
                [ParagraphPlugin.key]: withProps(PlateElement, {
                    as: 'p',
                    className: 'mb-4 last:mb-0 leading-relaxed',
                }),
                [LinkPlugin.key]: LinkElement,
                [SuggestionPlugin.key]: SuggestionLeaf,
                [CommentsPlugin.key]: CommentLeaf,
                [AIPlugin.key]: AILeaf,
                [FootnoteEntryPlugin.key]: FootnoteEntryElement,
                [CitationPlugin.key]: CitationElement,
                [CitationInputPlugin.key]: CitationInputElement,
                [ShortcutPlugin.key]: ShortcutElement,
                [ShortcutInputPlugin.key]: ShortcutInputElement,
            }
        }
    }, []);

    return editor;
}
